import { getGifBuilder } from './GifBuilder';
import { describeImageTiming } from './Timeline';
import { parseTransparencyColor } from './TransparencyColor';

const yieldToBrowser = () => new Promise(resolve => setTimeout(resolve, 0));
const checkCancelled = signal => {
  if (signal?.aborted) throw new DOMException('Export cancelled', 'AbortError');
};

// Composite and encode directly, independent of preview playback and animation ticks.
// Reuse one canvas and yield between bounded batches rather than retaining RGBA frames.
export const exportGif = async ({
  images, timeline, width, height, backgroundColor, transparentColor, transparencyTolerance,
  keepPreviousFrames = false, signal, onProgress = () => {},
  repeat = images.some(image => image.frames.length > 1 && describeImageTiming(image).playCount > 0) ? -1 : 0,
}) => {
  checkCancelled(signal);
  if (!timeline.length) throw new Error('There are no frames to export.');
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('Unable to create the export canvas.');
  const builder = getGifBuilder(width, height, transparentColor, keepPreviousFrames, repeat, transparencyTolerance);
  const hasColorKey = parseTransparencyColor(transparentColor) !== null;
  onProgress(0);
  // Let the save dialog paint before beginning CPU-intensive work.
  await yieldToBrowser();
  let nextFrame = 0;
  while (nextFrame < timeline.length) {
    const start = performance.now();
    let batchSize = 0;
    do {
      checkCancelled(signal);
      const entry = timeline[nextFrame];
      context.clearRect(0, 0, width, height);
      // With no color key, preserve the source alpha instead of the preview background.
      if (hasColorKey) {
        context.fillStyle = backgroundColor;
        context.fillRect(0, 0, width, height);
      }
      images.forEach((image, index) => {
        const frame = image.frames[entry.imageIndices[index]];
        if (frame && !frame.isEmpty) {
          context.drawImage(frame.image, image.x + frame.x, image.y + frame.y,
            frame.width, frame.height);
        }
      });
      builder.addFrame(canvas, { delay: entry.delay });
      nextFrame++;
      batchSize++;
    } while (nextFrame < timeline.length && batchSize < 32 && performance.now() - start < 12);
    onProgress(Math.round(nextFrame / timeline.length * 100));
    if (nextFrame < timeline.length) await yieldToBrowser();
  }
  checkCancelled(signal);
  return new Promise(resolve => {
    builder.on('finished', resolve);
    builder.render();
  });
};
